<template>
  <div class="user-group-edit-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-edit"></i>
          <span>{{ $t('userManagement.editGroup') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button @click="handleBack" size="small">
            <i class="el-icon-back"></i> {{ $t('common.back') }}
          </el-button>
        </div>
      </div>
    </div>

    <el-card class="user-group-edit-content" shadow="hover">
      <el-form
        :model="groupForm"
        :rules="groupFormRules"
        ref="groupForm"
        label-width="120px"
        class="user-group-edit-form"
      >
        <el-form-item :label="$t('userManagement.groupName')" prop="name">
          <el-input
            v-model="groupForm.name"
            :placeholder="$t('userManagement.enterGroupName')"
            maxlength="50"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('userManagement.description')" prop="description">
          <el-input
            v-model="groupForm.description"
            type="textarea"
            rows="4"
            :placeholder="$t('userManagement.enterGroupDescription')"
            maxlength="200"
            show-word-limit
          ></el-input>
        </el-form-item>

        <!-- 用户分配 -->
        <el-form-item :label="$t('userManagement.assignedUsers')" prop="user_ids">
          <div class="user-selection-wrapper">
            <el-select
              v-model="groupForm.user_ids"
              multiple
              filterable
              remote
              reserve-keyword
              :placeholder="$t('userManagement.selectUsersToAssign')"
              :remote-method="remoteSearchUsers"
              :loading="userLoading"
              style="width: 100%; flex: 1;"
            >
              <el-option
                v-for="user in remoteUsers"
                :key="user.userid"
                :label="user.username"
                :value="user.userid"
              >
                <span class="username">{{ user.username }}</span>
                <span class="email">({{ user.email }})</span>
              </el-option>
            </el-select>
            <el-button 
              type="primary" 
              @click="handleAddUsers" 
              class="add-user-btn"
              v-if="hasAddUserPermission"
            >
              <i class="el-icon-plus"></i> {{ $t('userManagement.addUsers') }}
            </el-button>
          </div>
          <div class="user-selection-hint">{{ $t('userManagement.searchUserHint') }}</div>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSubmit" v-if="hasPermission('sys:usergroup:update')">
            {{ $t('common.save') }}
          </el-button>
          <el-button @click="handleCancel">{{ $t('common.cancel') }}</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 添加用户对话框 -->
    <el-dialog
      :title="$t('userManagement.addUsersToGroup')"
      :visible.sync="addUsersDialogVisible"
      width="800px"
      :close-on-click-modal="false"
      append-to-body
    >
      <user-group-add-users
        v-if="addUsersDialogVisible"
        :exclude-user-ids="groupForm.user_ids"
        @success="handleAddUsersSuccess"
        @close="addUsersDialogVisible = false"
      />
    </el-dialog>
  </div>
</template>

<script>
import { getUserList } from '@/apis/user/user.js'
import UserGroupAddUsers from './UserGroupAddUsers.vue'
import { hasPermission } from '@/utils/permission'

export default {
  name: 'UserGroupEdit',
  components: {
    UserGroupAddUsers
  },
  data() {
    return {
      groupForm: {
        id: '',
        name: '',
        description: '',
        user_ids: []
      },
      groupFormRules: {
        name: [
          { required: true, message: this.$t('userManagement.groupNameRequired'), trigger: 'blur' },
          { min: 2, max: 50, message: this.$t('userManagement.groupNameLength'), trigger: 'blur' }
        ],
        description: [
          { max: 200, message: this.$t('userManagement.groupDescriptionLength'), trigger: 'blur' }
        ],
        user_ids: [
          { type: 'array', required: false, trigger: 'change' }
        ]
      },
      userLoading: false,
      remoteUsers: [],
      addUsersDialogVisible: false
    }
  },
  computed: {
    hasAddUserPermission() {
      return this.hasPermission('sys:usergroup:update')
    }
  },
  created() {
    this.loadGroupData()
  },
  methods: {
    hasPermission,
    // 加载分组数据
    async loadGroupData() {
      const groupId = this.$route.params.id
      let group = this.$store.getters.userGroupById(groupId)
      
      if (!group) {
        try {
          group = await this.$store.dispatch('fetchUserGroup', groupId)
        } catch (error) {
          console.error('Failed to fetch group:', error)
        }
      }
      
      if (group) {
        this.groupForm = {
          id: group.id,
          name: group.name,
          description: group.description,
          user_ids: (group.user_ids || []).map(id => String(id))
        }
        // If users list is available in group object, we can pre-populate remoteUsers
        // to show labels correctly in the select box for initial value
        if (group.users && group.users.length > 0) {
           this.remoteUsers = group.users.map(u => ({
             ...u,
             userid: u.userId || u.userid || u.id || ''
           }))
        }
      } else {
        this.$message.error(this.$t('userManagement.groupNotFound'))
        this.handleBack()
      }
    },
    
    // 返回上一页
    handleBack() {
      this.$router.back()
    },
    
    // 取消
    handleCancel() {
      this.handleBack()
    },
    
    // 重置表单
    handleResetForm() {
      this.loadGroupData()
    },
    
    // 远程搜索用户
    async remoteSearchUsers(query) {
      if (query !== '') {
        this.userLoading = true
        try {
          const res = await getUserList({ username: query, page: 1, limit: 20 })
          // request.js 已经处理了 code !== 0 的情况，直接返回 data
          if (res) {
            const rawUsers = res.list || res.records || (Array.isArray(res) ? res : [])
            const newUsers = rawUsers.map(u => ({
              ...u,
              userid: u.userId || u.userid || u.id || ''
            }))
            
            // 合并并去重，保留已选中的用户
            const selectedUsers = this.remoteUsers.filter(u => this.groupForm.user_ids.includes(u.userid))
            const combined = [...selectedUsers, ...newUsers]
            const uniqueMap = new Map()
            combined.forEach(u => uniqueMap.set(String(u.userid), u))
            this.remoteUsers = Array.from(uniqueMap.values())
          }
        } catch (error) {
          console.error('Search users failed:', error)
        } finally {
          this.userLoading = false
        }
      }
    },

    // 打开添加用户对话框
    handleAddUsers() {
      this.addUsersDialogVisible = true
    },

    // 添加用户成功回调
    handleAddUsersSuccess(data) {
      if (data.success && data.selectedUsers) {
        // 获取新选择的用户 ID
        const newUserIds = data.selectedUsers.map(u => String(u.userid))
        
        // 更新表单中的已选用户 ID（去重）
        const combinedIds = [...this.groupForm.user_ids.map(id => String(id)), ...newUserIds]
        this.groupForm.user_ids = [...new Set(combinedIds)]
        
        // 同时更新 remoteUsers 列表，确保 el-select 能显示这些用户的 label
        const combinedUsers = [...this.remoteUsers, ...data.selectedUsers]
        const uniqueMap = new Map()
        combinedUsers.forEach(u => uniqueMap.set(String(u.userid), u))
        this.remoteUsers = Array.from(uniqueMap.values())
        
        this.addUsersDialogVisible = false
        this.$message.success(this.$t('userManagement.addUsersSuccess'))
      }
    },
    
    // 提交表单
    handleSubmit() {
      this.$refs.groupForm.validate((valid) => {
        if (valid) {
          // 更新分组
          this.$store.dispatch('updateUserGroup', {
            id: this.groupForm.id,
            name: this.groupForm.name,
            description: this.groupForm.description,
            user_ids: this.groupForm.user_ids
          }).then(() => {
            this.$message.success(this.$t('userManagement.groupUpdateSuccess'))
            this.handleBack()
          }).catch(err => {
            this.$message.error(this.$t('userManagement.groupUpdateError'))
          })
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style scoped>
.user-group-edit-container {
  padding: 0;
}

.control-panel {
  margin-bottom: 24px;
}

.control-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #606266;
  font-size: 14px;
  
  i {
    font-size: 18px;
    color: #409eff;
  }
}

.action-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.user-group-edit-form {
  max-width: 800px;
}

.user-selection-wrapper {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.add-user-btn {
  white-space: nowrap;
}

.user-selection-hint {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
  line-height: 1.2;
}

.username {
  font-weight: 500;
}

.email {
  color: #606266;
  margin-left: 10px;
}
</style>